import { supabase } from '@/lib/supabase/client';

export interface AnalyticsMetrics {
  totalVisits: number;
  dailyVisits: number;
  topPages: { page: string; visits: number }[];
  trafficSources: { source: string; visits: number }[];
}

export async function getAnalyticsMetrics(): Promise<AnalyticsMetrics> {
  try {
    // Get total visits
    const { count: totalVisits } = await supabase
      .from('analytics')
      .select('*', { count: 'exact', head: true });

    // Get today's visits
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const { count: dailyVisits } = await supabase
      .from('analytics')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', today.toISOString());

    // Get top pages
    const { data: pagesData } = await supabase
      .from('analytics')
      .select('page_path')
      .gte('created_at', today.toISOString());

    const pageVisits = (pagesData || []).reduce((acc: Record<string, number>, item) => {
      acc[item.page_path] = (acc[item.page_path] || 0) + 1;
      return acc;
    }, {});

    const topPages = Object.entries(pageVisits)
      .map(([page, visits]) => ({ page, visits: visits as number }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 5);

    // Get traffic sources
    const { data: sourcesData } = await supabase
      .from('analytics')
      .select('traffic_source')
      .gte('created_at', today.toISOString());

    const sourceVisits = (sourcesData || []).reduce((acc: Record<string, number>, item) => {
      acc[item.traffic_source] = (acc[item.traffic_source] || 0) + 1;
      return acc;
    }, {});

    const trafficSources = Object.entries(sourceVisits)
      .map(([source, visits]) => ({ source, visits: visits as number }))
      .sort((a, b) => b.visits - a.visits);

    return {
      totalVisits: totalVisits || 0,
      dailyVisits: dailyVisits || 0,
      topPages,
      trafficSources
    };
  } catch (error) {
    console.error('Error fetching analytics metrics:', error);
    return {
      totalVisits: 0,
      dailyVisits: 0,
      topPages: [],
      trafficSources: []
    };
  }
}